import { useContext, useCallback } from 'react';
import _get from 'lodash/get';
import { AjaxContext, AppContext, formatListData, UserContext, EMPTY_ARRAY } from 'commons-ui';
import { UserDetailsContext } from 'context/userDetails';
import { PAGE_NAMES, USER_PROFILE_RESOURCES } from 'constant';
import { EURL } from 'enum';
import { IContentProps, IFormData, IDataObj, IVpaDetails, IBankDetails } from './types';

import { ViewPayoutRecordsParams } from '../../../../models/kycDetails.model';

const useService = () => {
	const {
		app: { appName },
		showAlert
	} = useContext(AppContext);
	const { postData } = useContext(AjaxContext);
	const {
		selectedUser: { uid }
	} = useContext(UserDetailsContext);
	const { user } = useContext(UserContext);
	const getBankOrVpaDetails = useCallback(async (isVpa: boolean, isBank: boolean) => {
		try {
			const result = await postData({
				endpoint: `/dashboard/getPayoutRecords/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.kycDetails}-getPayoutRecords`,
				queryParams: {
					appName
				},
				body: { uid, isVpa, isBank }
			});
			const bankOrVpa: string = isBank ? 'bank' : 'vpa';
			if (!result.data.success) {
				showAlert(result.data.error || `Error in fetching ${bankOrVpa} details.`, { type: 'error' });
			}
			const data = _get(result, ['data', 'data'], EMPTY_ARRAY);
			const listData = formatListData(data);

			return { rowCount: listData.length, data: listData };
		} catch (error: any) {
			return { rowCount: 0, data: [] };
		}
	}, []);

	const viewPayoutRecords = useCallback(async (params: ViewPayoutRecordsParams) => {
		try {
			const result = await postData({
				endpoint: EURL.VIEW_PAYOUT_RECORDS,
				queryParams: {
					appName
				},
				body: params
			});

			if (!result.data.success) {
				showAlert(result.data.error || 'Error in viewing payout records.', { type: 'error' });
				return { success: false };
			}

			const data = _get(result, ['data', 'data'], EMPTY_ARRAY);
			return { success: true, data };
		} catch (error: any) {
			showAlert('Error in viewing payout records.', { type: 'error' });
			return { success: false };
		}
	}, []);

	const linkBankDetails = useCallback(async (props: IContentProps, formData: IFormData) => {
		let { rowData } = props;
		const { isVpa, isBank } = props;
		const dataObj: IDataObj = {};
		if (isVpa) {
			rowData = <IVpaDetails>rowData;
			dataObj.upiId = rowData.upiId;
			dataObj.recordId = rowData.id;
		} else {
			rowData = <IBankDetails>rowData;
			dataObj.accountNumber = rowData.accountNumber;
			dataObj.ifscCode = rowData.ifscCode;
			dataObj.recordId = rowData.id;
		}

		const result = await postData({
			endpoint: `/dashboard/activatePayoutRecord/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.kycDetails}-activatePayoutRecord`,
			queryParams: {
				appName
			},
			body: {
				data: dataObj,
				uid,
				agentName: user?.userName,
				agentID: user?.email,
				actionOwner: 'CRM',
				...formData,
				isVpa,
				isBank
			}
		});
		const bankOrVpa: string = isBank ? 'Bank' : 'Vpa';
		if (result.data.success) {
			showAlert(`${bankOrVpa} details linked successfully.`);
		} else {
			showAlert(result.data.externalMessage || `Error in linking ${bankOrVpa} account.`, { type: 'error' });
		}

		return result;
	}, []);

	return { getBankOrVpaDetails, viewPayoutRecords, linkBankDetails };
};

export default useService;
